import { useSuspenseQuery } from '@apollo/client/react';
import { useRef, useState, startTransition } from 'react';
import { FileX, Car, MailCheck } from 'lucide-react';

import { AssetReportGroupsDocument } from 'src/graphql/actions/reports/assetReportGroups.query';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { withSuspense } from 'src/hoc';
import { EmptyBlock } from '@/components/ui/empty-block';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Paginator from '@/components/ui/paginator';
import { softwareEscrowAddonsDictionary, AddOnTypeEnum } from 'src/shared/entities/verification-addons';
import { LevelCard } from 'src/widgets/VerificationAddOns';
import env from 'env';
import { REPORTS_LIMIT } from 'src/shared/constants';

import { AssetReport, ReportSearch, Header } from '.';

import type { IAssetReportGroupsQueryVariables } from 'src/graphql/actions/reports/assetReportGroups.query';

import RequestCertifiedAction from './RequestCertifiedAction';

const VerifiedReports = withSuspense(() => {
  const {
    softwareEscrow: { id: softwareEscrowId },
  } = useSoftwareEscrow();
  const [variables, setVariables] = useState<Partial<IAssetReportGroupsQueryVariables>>({
    page: 1,
  });
  const [isUnsupportedAsset, setIsUnsupportedAsset] = useState(false);
  const {
    data: { assetReportGroups },
  } = useSuspenseQuery(AssetReportGroupsDocument, {
    variables: {
      ...variables,
      softwareEscrowId,
      perPage: REPORTS_LIMIT,
    },
  });
  const totalNodesCount = useRef(assetReportGroups.nodesCount);

  if (!totalNodesCount.current)
    return (
      <EmptyBlock
        icon={Car}
        title="Reports are coming your way and your escrow will be verified in no time!"
        description={`Reports will be available at the beginning of each month and added to the owner’s subscription.\nWe’ll contact the owner to confirm the add-on acquisition.`}
        classes={{
          title: 'text-base',
          card: 'absolute top-0 left-0 w-full h-full [&>div]:h-full',
          content: 'h-full flex flex-col justify-center',
        }}
      />
    );

  const onPageChange = (page: number) =>
    startTransition(() =>
      setVariables((prev) => ({
        ...prev,
        page,
      })),
    );

  return (
    <div className="space-y-6">
      <ReportSearch setVariables={setVariables} setUnsupportedAsset={setIsUnsupportedAsset} />
      <div className="max-w-[976px] mx-auto space-y-6">
        {isUnsupportedAsset && (
          <EmptyBlock
            title="Asset type not supported"
            description="Automated verification reports are only available for Git-based assets."
            icon={FileX}
            classes={{
              title: 'text-base',
              card: 'absolute top-16 bottom-0 left-0 w-full [&>div]:h-full',
              content: 'h-full flex flex-col justify-center',
            }}
          />
        )}
        {!isUnsupportedAsset ? (
          assetReportGroups.nodes.length ? (
            assetReportGroups.nodes.map((assetReport) => <AssetReport key={assetReport.id} assetReport={assetReport} />)
          ) : (
            <EmptyBlock
              title="No report found."
              description="This report does not exist. Please use a different filter."
              icon={FileX}
              classes={{
                title: 'text-base',
                card: 'absolute top-16 bottom-0 left-0 w-full [&>div]:h-full',
                content: 'h-full flex flex-col justify-center',
              }}
            />
          )
        ) : null}

        {!isUnsupportedAsset && Boolean(assetReportGroups.nodes.length) && (
          <Paginator
            currentPage={variables.page ?? 1}
            totalPages={assetReportGroups.pagesCount}
            onPageChange={onPageChange}
            showPreviousNext
            hideOnSinglePage
          />
        )}
      </div>
    </div>
  );
});

const CertifiedReports = () => {
  const {
    softwareEscrow: { certificationCertified },
  } = useSoftwareEscrow();

  if (certificationCertified)
    return (
      <EmptyBlock
        title="Check your inbox! We’ve created a certified report and sent it to your email."
        description={
          <>
            If you haven’t received it, please contact us at{' '}
            <a href={`mailto:${env.CODEKEEPER_SERVICE_EMAIL}`} className="text-muted-foreground! hover:underline!">
              service@codekeeper.co
            </a>
          </>
        }
        icon={MailCheck}
        classes={{
          title: 'text-base',
          card: 'absolute top-0 bottom-0 left-0 w-full [&>div]:h-full',
          content: 'h-full flex flex-col justify-center',
        }}
      />
    );

  return (
    <div className="max-w-[600px] mx-auto">
      <LevelCard {...softwareEscrowAddonsDictionary[AddOnTypeEnum.CERTIFIED]} actionNode={<RequestCertifiedAction />} />
    </div>
  );
};

const ReportsContainer = () => {
  return (
    <div className="container mx-auto min-h-full flex flex-col">
      <Header className="shrink-0" />
      <Tabs defaultValue="verified" className="grow-1 gap-6">
        <TabsList className="w-full justify-start border">
          <TabsTrigger value="verified" className="flex-0">
            Verified reports
          </TabsTrigger>
          <TabsTrigger value="certified" className="flex-0">
            Certified reports
          </TabsTrigger>
        </TabsList>
        <TabsContent value="verified" className="relative">
          <VerifiedReports />
        </TabsContent>
        <TabsContent value="certified" className="relative">
          <CertifiedReports />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsContainer;
